import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import LoadingDataDiv from "../common/LoadingDataDiv";
import { toastError, toastSuccess } from "../../../helpers/Toast";
import WorkScheduleEditor from "./WorkScheduleEditor";
import SchedulePrintDialog from "./SchedulePrintDialog";
import ScheduleGenerationProgress from "./ScheduleGenerationProgress";
import "../../../css/Scheduler.css";

const dateToString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentWeek = () => {
  const now = new Date();
  const day = now.getDay() || 7;
  const monday = new Date(now);
  monday.setDate(now.getDate() - day + 1);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { dateFrom: dateToString(monday), dateTo: dateToString(sunday) };
};


const schedulePeriodLabels = {
  DAILY: "Dzienny",
  WEEKEND: "Weekendowy",
  WEEKLY: "Tygodniowy",
  BIWEEKLY: "Dwutygodniowy",
  MONTHLY: "Miesięczny",
  CUSTOM: "Dowolny okres"
};

const parseDateString = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const addDaysToDateString = (value, days) => {
  const date = parseDateString(value);
  if (!date) return "";
  date.setDate(date.getDate() + days);
  return dateToString(date);
};

const dateRangeLength = (dateFrom, dateTo) => {
  const from = parseDateString(dateFrom);
  const to = parseDateString(dateTo);
  if (!from || !to) return 0;
  return Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
};

const wait = (milliseconds) => new Promise(resolve => window.setTimeout(resolve, milliseconds));

const createGenerationJobId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

const getIsoWeekValue = (inputDate = new Date()) => {
  const date = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());
  const day = date.getDay() || 7;
  date.setDate(date.getDate() + 4 - day);
  const isoYear = date.getFullYear();
  const yearStart = new Date(isoYear, 0, 1);
  const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return `${isoYear}-W${String(week).padStart(2, "0")}`;
};

const isoWeekToMonday = (weekValue) => {
  const match = /^(\d{4})-W(\d{2})$/.exec(weekValue || "");
  if (!match) return null;
  const year = Number(match[1]);
  const week = Number(match[2]);
  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay() || 7;
  const monday = new Date(year, 0, 4 - jan4Day + 1);
  monday.setDate(monday.getDate() + (week - 1) * 7);
  return monday;
};

const getSaturday = (inputDate = new Date()) => {
  const date = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());
  const daysUntilSaturday = (6 - date.getDay() + 7) % 7;
  date.setDate(date.getDate() + daysUntilSaturday);
  return date;
};

const createNewScheduleForm = (initialWeek) => {
  const today = new Date();
  return {
    name: "",
    departmentId: "",
    periodType: "WEEKLY",
    periodDate: dateToString(today),
    periodWeekend: dateToString(getSaturday(today)),
    periodWeek: getIsoWeekValue(today),
    periodMonth: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`,
    dateFrom: initialWeek.dateFrom,
    dateTo: initialWeek.dateTo,
    resourcePlanId: ""
  };
};

const resolveScheduleRange = (form) => {
  switch (form.periodType) {
    case "DAILY": {
      if (!form.periodDate) throw new Error("Wybierz dzień grafiku.");
      return {dateFrom: form.periodDate, dateTo: form.periodDate};
    }
    case "WEEKEND": {
      const saturday = parseDateString(form.periodWeekend);
      if (!saturday) throw new Error("Wybierz weekend.");
      if (saturday.getDay() !== 6) throw new Error("Dla grafiku weekendowego wybierz sobotę.");
      const sunday = new Date(saturday);
      sunday.setDate(saturday.getDate() + 1);
      return {dateFrom: dateToString(saturday), dateTo: dateToString(sunday)};
    }
    case "WEEKLY":
    case "BIWEEKLY": {
      const monday = isoWeekToMonday(form.periodWeek);
      if (!monday) throw new Error("Wybierz tydzień grafiku.");
      const end = new Date(monday);
      end.setDate(monday.getDate() + (form.periodType === "BIWEEKLY" ? 13 : 6));
      return {dateFrom: dateToString(monday), dateTo: dateToString(end)};
    }
    case "MONTHLY": {
      const match = /^(\d{4})-(\d{2})$/.exec(form.periodMonth || "");
      if (!match) throw new Error("Wybierz miesiąc grafiku.");
      const year = Number(match[1]);
      const month = Number(match[2]);
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);
      return {dateFrom: dateToString(firstDay), dateTo: dateToString(lastDay)};
    }
    case "CUSTOM": {
      if (!form.dateFrom || !form.dateTo) throw new Error("Podaj początek i koniec okresu grafiku.");
      if (form.dateTo < form.dateFrom) throw new Error("Koniec okresu grafiku nie może być wcześniejszy niż początek.");
      return {dateFrom: form.dateFrom, dateTo: form.dateTo};
    }
    default:
      throw new Error("Nieznany typ okresu grafiku.");
  }
};

const statusLabels = {
  DRAFT: "Roboczy",
  PUBLISHED: "Opublikowany",
  ARCHIVED: "Archiwalny"
};

const WorkSchedule = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  const initialWeek = useMemo(() => getCurrentWeek(), []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [referenceData, setReferenceData] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [generationPlans, setGenerationPlans] = useState([]);
  const [generationResult, setGenerationResult] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(null);
  const [filters, setFilters] = useState({ departmentId: "", ...initialWeek });
  const [newSchedule, setNewSchedule] = useState(() => createNewScheduleForm(initialWeek));
  const [showNewSchedule, setShowNewSchedule] = useState(false);
  const [creationPlans, setCreationPlans] = useState([]);
  const [creationPlansLoading, setCreationPlansLoading] = useState(false);
  const [preferredGenerationPlanId, setPreferredGenerationPlanId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [publishWarnings, setPublishWarnings] = useState([]);
  const [showCopyDraft, setShowCopyDraft] = useState(false);
  const [copyDraftName, setCopyDraftName] = useState("");
  const [copyDraftDateFrom, setCopyDraftDateFrom] = useState("");
  const [copyDraftDateTo, setCopyDraftDateTo] = useState("");

  const handleUnauthorized = (error) => {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      navigate("/login", { state: { from: location }, replace: true });
      return true;
    }
    return false;
  };

  const getErrorMessage = (error) => {
    const data = error?.response?.data;
    if (typeof data === "string") return data;
    if (data?.message) return data.message;
    if (error?.message) return error.message;
    return "Nie udało się wykonać operacji.";
  };

  const loadSchedules = async (nextFilters = filters) => {
    const params = {};
    if (nextFilters.departmentId) params.departmentId = Number(nextFilters.departmentId);
    if (nextFilters.dateFrom) params.dateFrom = nextFilters.dateFrom;
    if (nextFilters.dateTo) params.dateTo = nextFilters.dateTo;
    const response = await axiosPrivate.get("/work-schedules", { params });
    setSchedules(response.data);
    return response.data;
  };

  const loadSchedule = async (id) => {
    const response = await axiosPrivate.get(`/work-schedules/${id}`);
    setSelectedSchedule(response.data);
    return response.data;
  };

  const loadReferenceData = async (departmentId) => {
    if (!departmentId) {
      setReferenceData(null);
      return;
    }
    const response = await axiosPrivate.get("/work-schedules/reference-data", { params: { departmentId } });
    setReferenceData(response.data);
  };

  const loadCalendarData = async (scheduleId, dateFrom, dateTo) => {
    if (!scheduleId) {
      setCalendarData(null);
      return null;
    }
    const params = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    const response = await axiosPrivate.get(`/work-schedules/${scheduleId}/calendar`, { params });
    setCalendarData(response.data);
    return response.data;
  };

  const loadGenerationPlans = async (scheduleId) => {
    if (!scheduleId) {
      setGenerationPlans([]);
      return;
    }
    const response = await axiosPrivate.get(`/work-schedules/${scheduleId}/generation-plans`);
    setGenerationPlans(response.data);
  };

  const loadCreationPlans = async (departmentId) => {
    if (!departmentId) {
      setCreationPlans([]);
      return;
    }
    setCreationPlansLoading(true);
    try {
      const response = await axiosPrivate.get("/work-schedules/generation-plans", {
        params: { departmentId: Number(departmentId) }
      });
      setCreationPlans(response.data ?? []);
    } finally {
      setCreationPlansLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const departmentsResponse = await axiosPrivate.get("/schedule-access/departments");
        if (!mounted) return;
        setDepartments(departmentsResponse.data);
        await loadSchedules(filters);
      } catch (error) {
        if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!showNewSchedule || !newSchedule.departmentId) {
      setCreationPlans([]);
      return;
    }
    loadCreationPlans(newSchedule.departmentId).catch(error => {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
    });
  }, [showNewSchedule, newSchedule.departmentId]);

  useEffect(() => {
    if (!showArchived && selectedSchedule?.status === "ARCHIVED") {
      setSelectedSchedule(null);
      setGenerationResult(null);
    }
  }, [showArchived, selectedSchedule?.status]);

  useEffect(() => {
    if (!selectedSchedule?.id || !selectedSchedule?.departmentId) {
      setReferenceData(null);
      setCalendarData(null);
      setGenerationPlans([]);
      setGenerationResult(null);
      setGenerationProgress(current => current?.status === "RUNNING" ? current : null);
      return;
    }

    setGenerationPlans([]);
    setGenerationResult(current => current?.scheduleId === selectedSchedule.id ? current : null);
    setGenerationProgress(current => current?.scheduleId === selectedSchedule.id ? current : null);
    Promise.all([
      loadReferenceData(selectedSchedule.departmentId),
      loadGenerationPlans(selectedSchedule.id),
      loadCalendarData(selectedSchedule.id, selectedSchedule.dateFrom, selectedSchedule.dateTo)
    ]).catch(error => {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
    });
  }, [selectedSchedule?.id, selectedSchedule?.departmentId, selectedSchedule?.dateFrom, selectedSchedule?.dateTo]);

  useEffect(() => {
    if (!selectedSchedule?.id || selectedSchedule?.status !== "PUBLISHED") return undefined;
    const currentDate = dateToString(new Date());
    if (currentDate < selectedSchedule.dateFrom || currentDate > selectedSchedule.dateTo) return undefined;

    const timer = window.setInterval(() => {
      loadCalendarData(selectedSchedule.id, selectedSchedule.dateFrom, selectedSchedule.dateTo).catch(error => {
        if (!handleUnauthorized(error)) console.error(error);
      });
    }, 60000);

    return () => window.clearInterval(timer);
  }, [selectedSchedule?.id, selectedSchedule?.status, selectedSchedule?.dateFrom, selectedSchedule?.dateTo]);

  const plansForSelectedType = useMemo(
    () => creationPlans.filter(plan => plan.periodType === newSchedule.periodType),
    [creationPlans, newSchedule.periodType]
  );

  useEffect(() => {
    if (creationPlansLoading || plansForSelectedType.length === 0) return;
    const currentExists = plansForSelectedType.some(plan => String(plan.id) === String(newSchedule.resourcePlanId));
    if (currentExists) return;
    const selected = plansForSelectedType.find(plan => plan.departmentDefault) || null;
    setNewSchedule(current => ({...current, resourcePlanId: selected ? String(selected.id) : ""}));
  }, [creationPlansLoading, plansForSelectedType, newSchedule.resourcePlanId]);

  const selectedPeriodSupported = !!newSchedule.departmentId
    && !creationPlansLoading
    && plansForSelectedType.length > 0;

  const visibleSchedules = useMemo(
    () => showArchived ? schedules : schedules.filter(schedule => schedule.status !== "ARCHIVED"),
    [schedules, showArchived]
  );

  const createDisabledReason = !newSchedule.departmentId
    ? "Najpierw wybierz dział."
    : creationPlansLoading
      ? "Trwa sprawdzanie planów zapotrzebowania."
      : plansForSelectedType.length === 0
        ? `Brak aktywnego planu zapotrzebowania typu „${schedulePeriodLabels[newSchedule.periodType]}” dla wybranego działu.`
        : !newSchedule.resourcePlanId
          ? "Wybierz plan zapotrzebowania."
          : "";

  const applyFilters = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await loadSchedules(filters);
      setSelectedSchedule(null);
      setGenerationResult(null);
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const runGenerationRequest = async (scheduleId, payload) => {
    const progressJobId = createGenerationJobId();
    setGenerationProgress({
      jobId: progressJobId,
      scheduleId,
      status: "RUNNING",
      phase: "Uruchamianie generatora",
      currentItemLabel: null,
      totalItems: 0,
      completedItems: 0,
      percent: 1,
      elapsedSeconds: 0,
      estimatedRemainingSeconds: null,
      errorMessage: null
    });

    let polling = true;
    const pollProgress = async () => {
      while (polling) {
        try {
          const response = await axiosPrivate.get(`/work-schedules/${scheduleId}/generation-progress/${progressJobId}`);
          setGenerationProgress(response.data);
          if (["COMPLETED", "FAILED", "CANCELLED"].includes(response.data?.status)) break;
        } catch (error) {
          if (error?.response?.status !== 404 && !handleUnauthorized(error)) console.error(error);
        }
        await wait(750);
      }
    };

    const generationPromise = axiosPrivate.post(`/work-schedules/${scheduleId}/generate-period`, {
      ...payload,
      progressJobId
    });
    const pollingPromise = pollProgress();

    try {
      return await generationPromise;
    } finally {
      polling = false;
      await pollingPromise;
      try {
        const response = await axiosPrivate.get(`/work-schedules/${scheduleId}/generation-progress/${progressJobId}`);
        setGenerationProgress(response.data);
      } catch (error) {
        if (error?.response?.status !== 404 && !handleUnauthorized(error)) console.error(error);
      }
    }
  };

  const cancelGeneration = async () => {
    const progress = generationProgress;
    if (!progress?.jobId || !progress?.scheduleId || progress.status !== "RUNNING") return;

    setGenerationProgress(current => current?.jobId === progress.jobId ? {
      ...current,
      status: "CANCELLING",
      phase: "Anulowanie generowania",
      currentItemLabel: null,
      estimatedRemainingSeconds: null
    } : current);

    try {
      const response = await axiosPrivate.post(`/work-schedules/${progress.scheduleId}/generation-progress/${progress.jobId}/cancel`);
      setGenerationProgress(response.data);
    } catch (error) {
      if (handleUnauthorized(error)) return;
      setGenerationProgress(current => current?.jobId === progress.jobId && current?.status === "CANCELLING" ? {
        ...current,
        status: "RUNNING",
        phase: "Przydzielanie pracowników"
      } : current);
      toastError(getErrorMessage(error));
    }
  };

  const isGenerationCancelled = (error) => {
    const message = getErrorMessage(error);
    return error?.response?.status === 422 && message === "Generowanie grafiku zostało anulowane.";
  };

  const createSchedule = async (event) => {
    event.preventDefault();
    if (!selectedPeriodSupported || !newSchedule.resourcePlanId) return;
    setSaving(true);
    setGenerationProgress(null);
    let created = null;
    try {
      const range = resolveScheduleRange(newSchedule);
      const resourcePlanId = Number(newSchedule.resourcePlanId);
      const response = await axiosPrivate.post("/work-schedules", {
        name: newSchedule.name,
        departmentId: Number(newSchedule.departmentId),
        dateFrom: range.dateFrom,
        dateTo: range.dateTo
      });
      created = response.data;
      setPreferredGenerationPlanId(resourcePlanId);

      const generationResponse = await runGenerationRequest(created.id, {
        periodStart: range.dateFrom,
        resourcePlanId,
        replaceGenerated: true
      });

      await loadSchedules(filters);
      await loadSchedule(created.id);
      setGenerationResult(generationResponse.data);
      setShowNewSchedule(false);
      setNewSchedule(createNewScheduleForm(initialWeek));

      const generated = generationResponse.data?.generatedAssignments ?? 0;
      const shortages = generationResponse.data?.openShifts ?? 0;
      if (shortages > 0) {
        toastSuccess(`Utworzono grafik. Przydzieleni pracownicy: ${generated}. Braki obsady: ${shortages}.`);
      } else {
        toastSuccess(`Utworzono grafik i wygenerowano obsadę. Przydzieleni pracownicy: ${generated}.`);
      }
    } catch (error) {
      if (handleUnauthorized(error)) return;
      if (created?.id && isGenerationCancelled(error)) {
        try {
          await axiosPrivate.delete(`/work-schedules/${created.id}`);
          await loadSchedules(filters);
          setSelectedSchedule(null);
          setGenerationResult(null);
          setShowNewSchedule(false);
          setNewSchedule(createNewScheduleForm(initialWeek));
          toastSuccess("Tworzenie i generowanie zostało przerwane. Nowy grafik nie został zapisany.");
        } catch (cleanupError) {
          if (handleUnauthorized(cleanupError)) return;
          toastError(`Generowanie przerwano, ale nie udało się usunąć pustego grafiku: ${getErrorMessage(cleanupError)}`);
        }
      } else if (created?.id) {
        try {
          await loadSchedules(filters);
          await loadSchedule(created.id);
          setShowNewSchedule(false);
          setNewSchedule(createNewScheduleForm(initialWeek));
        } catch (reloadError) {
          if (handleUnauthorized(reloadError)) return;
        }
        toastError(`Grafik został utworzony, ale nie udało się wygenerować obsady: ${getErrorMessage(error)}`);
      } else {
        toastError(getErrorMessage(error));
      }
    } finally {
      setSaving(false);
    }
  };

  const saveSchedule = async (payload) => {
    setSaving(true);
    try {
      const response = await axiosPrivate.put(`/work-schedules/${selectedSchedule.id}`, payload);
      setSelectedSchedule(response.data);
      await loadSchedules(filters);
      toastSuccess("Zapisano dane grafiku.");
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setSaving(true);
    try {
      const response = await axiosPrivate.post(`/work-schedules/${selectedSchedule.id}/publish`);
      setSelectedSchedule(response.data);
      await loadSchedules(filters);
      setPublishWarnings([]);
      toastSuccess("Grafik został opublikowany.");
    } catch (error) {
      if (!handleUnauthorized(error)) {
        const message = getErrorMessage(error);
        if (error?.response?.status === 422 && message.startsWith("OSTRZEŻENIA_PUBLIKACJI")) {
          setPublishWarnings(
            message
              .replace(/^OSTRZEŻENIA_PUBLIKACJI\s*/, "")
              .split("\n")
              .map(line => line.trim())
              .filter(Boolean)
          );
        } else {
          toastError(message);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const publishWithRuleOverride = async () => {
    setSaving(true);
    try {
      const response = await axiosPrivate.post(
        `/work-schedules/${selectedSchedule.id}/publish`,
        null,
        { params: { overrideRuleViolations: true } }
      );
      setSelectedSchedule(response.data);
      await loadSchedules(filters);
      setPublishWarnings([]);
      toastSuccess("Grafik został opublikowany z zaakceptowanymi odstępstwami od reguł czasu pracy.");
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    setSaving(true);
    try {
      const response = await axiosPrivate.post(`/work-schedules/${selectedSchedule.id}/archive`);
      const archivedSchedule = response.data;
      setSelectedSchedule(showArchived ? archivedSchedule : null);
      await loadSchedules(filters);
      toastSuccess("Grafik został zarchiwizowany.");
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const openCopyDraftDialog = () => {
    if (!selectedSchedule || selectedSchedule.status !== "PUBLISHED") return;
    setCopyDraftName(`${selectedSchedule.name || "Grafik"} - kopia robocza`);
    setCopyDraftDateFrom(selectedSchedule.dateFrom || "");
    setCopyDraftDateTo(selectedSchedule.dateTo || "");
    setShowCopyDraft(true);
  };

  const changeCopyDraftDateFrom = value => {
    const sourceLength = dateRangeLength(selectedSchedule?.dateFrom, selectedSchedule?.dateTo);
    setCopyDraftDateFrom(value);
    if (value && sourceLength > 0) {
      setCopyDraftDateTo(addDaysToDateString(value, sourceLength - 1));
    }
  };

  const copyPublishedScheduleAsDraft = async () => {
    if (!selectedSchedule || selectedSchedule.status !== "PUBLISHED") return;
    setSaving(true);
    try {
      const response = await axiosPrivate.post(`/work-schedules/${selectedSchedule.id}/copy-as-draft`, {
        name: copyDraftName,
        dateFrom: copyDraftDateFrom,
        dateTo: copyDraftDateTo
      });
      setShowCopyDraft(false);
      setGenerationResult(null);
      await loadSchedules(filters);
      await loadSchedule(response.data.id);
      const copiedCount = response.data?.assignments?.length ?? 0;
      toastSuccess(`Utworzono grafik roboczy na podstawie opublikowanego grafiku. Skopiowano zmian: ${copiedCount}.`);
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const deleteSchedule = async () => {
    setSaving(true);
    try {
      await axiosPrivate.delete(`/work-schedules/${selectedSchedule.id}`);
      setSelectedSchedule(null);
      setPreferredGenerationPlanId(null);
      await loadSchedules(filters);
      toastSuccess("Grafik został usunięty.");
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const generatePeriod = async (payload) => {
    setSaving(true);
    setGenerationProgress(null);
    try {
      const response = await runGenerationRequest(selectedSchedule.id, payload);
      setGenerationResult(response.data);
      await loadSchedule(selectedSchedule.id);
      await loadCalendarData(selectedSchedule.id, selectedSchedule.dateFrom, selectedSchedule.dateTo);
      await loadSchedules(filters);
      const shortages = response.data?.openShifts ?? 0;
      const generated = response.data?.generatedAssignments ?? 0;
      if (shortages > 0) {
        toastSuccess(`Generowanie zakończone. Przydzieleni pracownicy: ${generated}. Braki obsady: ${shortages}.`);
      } else {
        toastSuccess(`Wygenerowano obsadę całego okresu. Przydzieleni pracownicy: ${generated}.`);
      }
      return response.data;
    } catch (error) {
      if (isGenerationCancelled(error)) {
        setGenerationResult(null);
        try {
          await loadSchedule(selectedSchedule.id);
          await loadCalendarData(selectedSchedule.id, selectedSchedule.dateFrom, selectedSchedule.dateTo);
          await loadSchedules(filters);
        } catch (reloadError) {
          if (handleUnauthorized(reloadError)) return null;
        }
        toastSuccess("Generowanie przerwane. Grafik nie został zmieniony.");
        return null;
      }
      setGenerationProgress(current => current?.status === "FAILED" ? current : current ? {
        ...current,
        status: "FAILED",
        phase: "Generowanie przerwane",
        errorMessage: getErrorMessage(error)
      } : current);
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAssignment = async (assignment, payload) => {
    setSaving(true);
    try {
      if (assignment) {
        await axiosPrivate.put(`/work-schedules/${selectedSchedule.id}/assignments/${assignment.id}`, payload);
      } else {
        await axiosPrivate.post(`/work-schedules/${selectedSchedule.id}/assignments`, payload);
      }
      await loadSchedule(selectedSchedule.id);
      await loadCalendarData(selectedSchedule.id, selectedSchedule.dateFrom, selectedSchedule.dateTo);
      await loadSchedules(filters);
      toastSuccess(assignment ? "Zapisano zmianę." : "Dodano zmianę.");
      return true;
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const deleteAssignment = async (assignmentId) => {
    setSaving(true);
    try {
      await axiosPrivate.delete(`/work-schedules/${selectedSchedule.id}/assignments/${assignmentId}`);
      await loadSchedule(selectedSchedule.id);
      await loadCalendarData(selectedSchedule.id, selectedSchedule.dateFrom, selectedSchedule.dateTo);
      await loadSchedules(filters);
      toastSuccess("Usunięto zmianę.");
      return true;
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAvailability = async (availability, payload) => {
    setSaving(true);
    try {
      if (availability?.id) {
        await axiosPrivate.put(`/employee-availabilities/${availability.id}`, payload);
      } else {
        await axiosPrivate.post("/employee-availabilities", payload);
      }
      await loadCalendarData(selectedSchedule.id, selectedSchedule.dateFrom, selectedSchedule.dateTo);
      toastSuccess(availability?.id ? "Zapisano dostępność." : "Dodano dostępność.");
      return true;
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const deleteAvailability = async (availabilityId) => {
    setSaving(true);
    try {
      await axiosPrivate.delete(`/employee-availabilities/${availabilityId}`);
      await loadCalendarData(selectedSchedule.id, selectedSchedule.dateFrom, selectedSchedule.dateTo);
      toastSuccess("Usunięto wpis dostępności.");
      return true;
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAbsence = async (absence, payload) => {
    setSaving(true);
    try {
      if (absence?.id) {
        await axiosPrivate.put(`/employee-absences/${absence.id}`, payload);
      } else {
        await axiosPrivate.post("/employee-absences", payload);
      }
      await loadCalendarData(selectedSchedule.id, selectedSchedule.dateFrom, selectedSchedule.dateTo);
      toastSuccess(absence?.id ? "Zapisano nieobecność." : "Dodano nieobecność.");
      return true;
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const deleteAbsence = async (absenceId) => {
    setSaving(true);
    try {
      await axiosPrivate.delete(`/employee-absences/${absenceId}`);
      await loadCalendarData(selectedSchedule.id, selectedSchedule.dateFrom, selectedSchedule.dateTo);
      toastSuccess("Usunięto nieobecność.");
      return true;
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const markUnexcusedAbsence = async payload => {
    setSaving(true);
    try {
      await axiosPrivate.post("/employee-absences/unexcused", payload);
      await loadCalendarData(selectedSchedule.id, selectedSchedule.dateFrom, selectedSchedule.dateTo);
      toastSuccess("Oznaczono nieobecność nieusprawiedliwioną.");
      return true;
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const refreshCalendar = async () => {
    if (!selectedSchedule?.id) return;
    try {
      await loadCalendarData(selectedSchedule.id, selectedSchedule.dateFrom, selectedSchedule.dateTo);
      toastSuccess("Odświeżono dane grafiku i RCP.");
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
    }
  };

  const reloadAfterDirectAssignmentChange = async () => {
    if (!selectedSchedule?.id) return;
    await loadSchedule(selectedSchedule.id);
    await loadCalendarData(selectedSchedule.id, selectedSchedule.dateFrom, selectedSchedule.dateTo);
    await loadSchedules(filters);
  };

  const openSchedule = async (id) => {
    setLoading(true);
    try {
      setPreferredGenerationPlanId(null);
      await loadSchedule(id);
    } catch (error) {
      if (!handleUnauthorized(error)) toastError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="layoutSidenav_content">
      <div className="app-component-card-header shadow d-flex justify-content-between align-items-center">
        <div>Grafik pracy</div>
        <div className="d-flex gap-2">
          {selectedSchedule && <Button variant="outline-primary" onClick={() => setShowPrint(true)}>Drukuj</Button>}
          <Button variant="primary" onClick={() => setShowNewSchedule(value => !value)}>{showNewSchedule ? "Anuluj" : "Nowy grafik"}</Button>
        </div>
      </div>

      <div className="container-fluid px-4 schedule-page">
        {showNewSchedule && (
          <form className="schedule-new-card shadow-sm" onSubmit={createSchedule}>
            <h6>Nowy grafik roboczy</h6>
            <div className="schedule-new-grid">
              <Form.Group>
                <Form.Label>Nazwa</Form.Label>
                <Form.Control value={newSchedule.name} onChange={event => setNewSchedule(current => ({ ...current, name: event.target.value }))} placeholder="Opcjonalna" />
              </Form.Group>
              <Form.Group>
                <Form.Label>Dział</Form.Label>
                <Form.Select value={newSchedule.departmentId} onChange={event => setNewSchedule(current => ({ ...current, departmentId: event.target.value, resourcePlanId: "" }))} required>
                  <option value="">Wybierz dział</option>
                  {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Typ okresu</Form.Label>
                <Form.Select
                  value={newSchedule.periodType}
                  onChange={event => setNewSchedule(current => ({ ...current, periodType: event.target.value, resourcePlanId: "" }))}
                >
                  {Object.entries(schedulePeriodLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              {newSchedule.periodType === "DAILY" && (
                <Form.Group>
                  <Form.Label>Dzień</Form.Label>
                  <Form.Control type="date" value={newSchedule.periodDate} onChange={event => setNewSchedule(current => ({ ...current, periodDate: event.target.value }))} required />
                </Form.Group>
              )}

              {newSchedule.periodType === "WEEKEND" && (
                <Form.Group>
                  <Form.Label>Weekend — wybierz sobotę</Form.Label>
                  <Form.Control type="date" value={newSchedule.periodWeekend} onChange={event => setNewSchedule(current => ({ ...current, periodWeekend: event.target.value }))} required />
                </Form.Group>
              )}

              {(newSchedule.periodType === "WEEKLY" || newSchedule.periodType === "BIWEEKLY") && (
                <Form.Group>
                  <Form.Label>{newSchedule.periodType === "BIWEEKLY" ? "Pierwszy tydzień okresu" : "Tydzień"}</Form.Label>
                  <Form.Control type="week" value={newSchedule.periodWeek} onChange={event => setNewSchedule(current => ({ ...current, periodWeek: event.target.value }))} required />
                </Form.Group>
              )}

              {newSchedule.periodType === "MONTHLY" && (
                <Form.Group>
                  <Form.Label>Miesiąc</Form.Label>
                  <Form.Control type="month" value={newSchedule.periodMonth} onChange={event => setNewSchedule(current => ({ ...current, periodMonth: event.target.value }))} required />
                </Form.Group>
              )}

              {newSchedule.periodType === "CUSTOM" && (
                <>
                  <Form.Group>
                    <Form.Label>Od</Form.Label>
                    <Form.Control type="date" value={newSchedule.dateFrom} onChange={event => setNewSchedule(current => ({ ...current, dateFrom: event.target.value }))} required />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Do</Form.Label>
                    <Form.Control type="date" value={newSchedule.dateTo} onChange={event => setNewSchedule(current => ({ ...current, dateTo: event.target.value }))} required />
                  </Form.Group>
                </>
              )}

              <Form.Group>
                <Form.Label>Plan zapotrzebowania</Form.Label>
                <Form.Select
                  value={newSchedule.resourcePlanId}
                  onChange={event => setNewSchedule(current => ({ ...current, resourcePlanId: event.target.value }))}
                  disabled={!newSchedule.departmentId || creationPlansLoading || plansForSelectedType.length === 0}
                  required
                >
                  <option value="">{creationPlansLoading ? "Sprawdzanie planów..." : "Wybierz plan"}</option>
                  {plansForSelectedType.map(plan => (
                    <option key={plan.id} value={plan.id}>{plan.name}{plan.departmentDefault ? " — domyślny dla działu" : ""}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <div className="schedule-new-submit">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{createDisabledReason || "Utwórz grafik roboczy"}</Tooltip>}
                >
                  <span className="d-inline-block">
                    <Button
                      type="submit"
                      disabled={saving || !selectedPeriodSupported || !newSchedule.resourcePlanId}
                      style={{ pointerEvents: saving || !selectedPeriodSupported || !newSchedule.resourcePlanId ? "none" : "auto" }}
                    >
                      Utwórz i wygeneruj
                    </Button>
                  </span>
                </OverlayTrigger>
              </div>
              {generationProgress?.status && (
                <div className="schedule-new-generation-progress">
                  <ScheduleGenerationProgress progress={generationProgress} onCancel={cancelGeneration} />
                </div>
              )}
            </div>
          </form>
        )}

        <form className="schedule-filters" onSubmit={applyFilters}>
          <Form.Select value={filters.departmentId} onChange={event => setFilters(current => ({ ...current, departmentId: event.target.value }))}>
            <option value="">Wszystkie działy</option>
            {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
          </Form.Select>
          <Form.Control type="date" value={filters.dateFrom} onChange={event => setFilters(current => ({ ...current, dateFrom: event.target.value }))} />
          <Form.Control type="date" value={filters.dateTo} onChange={event => setFilters(current => ({ ...current, dateTo: event.target.value }))} />
          <Button type="submit" variant="secondary">Filtruj</Button>
          <Form.Check
            type="checkbox"
            id="schedule-show-archived"
            className="schedule-show-archived"
            label="Pokaż zarchiwizowane"
            checked={showArchived}
            onChange={event => setShowArchived(event.target.checked)}
          />
        </form>

        {loading ? <LoadingDataDiv /> : (
          <div className="schedule-layout">
            <div className="schedule-list shadow-sm">
              <div className="schedule-list-heading">Grafiki</div>
              {visibleSchedules.length === 0 ? (
                <div className="schedule-empty">{showArchived ? "Brak grafików w wybranym okresie." : "Brak aktywnych grafików w wybranym okresie."}</div>
              ) : visibleSchedules.map(schedule => (
                <button
                  key={schedule.id}
                  type="button"
                  className={`schedule-list-item ${selectedSchedule?.id === schedule.id ? "active" : ""}`}
                  onClick={() => openSchedule(schedule.id)}
                >
                  <div className="schedule-list-title">{schedule.name}</div>
                  <div>{schedule.departmentName}</div>
                  <div>{schedule.dateFrom} – {schedule.dateTo}</div>
                  <div className="schedule-list-footer">
                    <span>{statusLabels[schedule.status] ?? schedule.status}</span>
                    <span>{schedule.assignmentCount} zmian</span>
                  </div>
                </button>
              ))}
            </div>

            <div className="schedule-content shadow-sm">
              {selectedSchedule ? (
                <WorkScheduleEditor
                  schedule={selectedSchedule}
                  departments={departments}
                  referenceData={referenceData}
                  calendarData={calendarData}
                  generationPlans={generationPlans}
                  generationResult={generationResult}
                  generationProgress={generationProgress}
                  onGeneratePeriod={generatePeriod}
                  onCancelGeneration={cancelGeneration}
                  onSaveSchedule={saveSchedule}
                  onPublish={publish}
                  onArchive={archive}
                  onDeleteSchedule={deleteSchedule}
                  onSaveAssignment={saveAssignment}
                  onDeleteAssignment={deleteAssignment}
                  onSaveAvailability={saveAvailability}
                  onDeleteAvailability={deleteAvailability}
                  onSaveAbsence={saveAbsence}
                  onDeleteAbsence={deleteAbsence}
                  onMarkUnexcusedAbsence={markUnexcusedAbsence}
                  onRefreshCalendar={refreshCalendar}
                  onDirectAssignmentChanged={reloadAfterDirectAssignmentChange}
                  onCopyAsDraft={openCopyDraftDialog}
                  saving={saving}
                  preferredGenerationPlanId={preferredGenerationPlanId}
                />
              ) : (
                <div className="schedule-empty schedule-empty-main">Wybierz grafik z listy albo utwórz nowy.</div>
              )}
            </div>
          </div>
        )}
      </div>

      <Modal
        show={publishWarnings.length > 0}
        onHide={() => !saving && setPublishWarnings([])}
        backdrop="static"
        keyboard={!saving}
        centered
        size="lg"
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Ostrzeżenia przed publikacją grafiku</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Grafik zawiera zmiany naruszające reguły czasu pracy. Możesz wrócić do grafiku i poprawić obsadę albo świadomie zaakceptować odstępstwa i opublikować grafik.
          </p>
          <div className="alert alert-warning mb-0">
            <strong>Wykryte ostrzeżenia:</strong>
            <ul className="mt-2 mb-0">
              {publishWarnings.map((warning, index) => (
                <li key={`${index}-${warning}`}>{warning}</li>
              ))}
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={saving} onClick={() => setPublishWarnings([])}>
            Wróć i popraw grafik
          </Button>
          <Button variant="warning" disabled={saving} onClick={publishWithRuleOverride}>
            Akceptuję ostrzeżenia i publikuję
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCopyDraft}
        onHide={() => !saving && setShowCopyDraft(false)}
        backdrop="static"
        keyboard={!saving}
        centered
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Utwórz grafik roboczy z opublikowanego</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Kopiowany jest aktualnie otwarty grafik <strong>{selectedSchedule?.name}</strong>. Możesz wskazać nowy okres; wszystkie zmiany zostaną przesunięte o tę samą liczbę dni względem początku grafiku źródłowego.
          </p>
          <div className="schedule-copy-draft-grid">
            <Form.Group className="schedule-copy-draft-name">
              <Form.Label>Nazwa nowego grafiku</Form.Label>
              <Form.Control
                value={copyDraftName}
                maxLength={150}
                onChange={event => setCopyDraftName(event.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Od</Form.Label>
              <Form.Control
                type="date"
                value={copyDraftDateFrom}
                onChange={event => changeCopyDraftDateFrom(event.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Do</Form.Label>
              <Form.Control
                type="date"
                value={copyDraftDateTo}
                min={copyDraftDateFrom || undefined}
                onChange={event => setCopyDraftDateTo(event.target.value)}
              />
            </Form.Group>
          </div>
          <div className="form-text mt-2">
            Zakres kopii musi mieć taką samą liczbę dni jak grafik źródłowy ({dateRangeLength(selectedSchedule?.dateFrom, selectedSchedule?.dateTo)}). Zmiana daty „Od” automatycznie przesuwa datę „Do”. Zaakceptowane wcześniej odstępstwa od reguł czasu pracy nie są kopiowane jako zaakceptowane.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={saving} onClick={() => setShowCopyDraft(false)}>Anuluj</Button>
          <Button
            variant="primary"
            disabled={saving || !copyDraftName.trim() || !copyDraftDateFrom || !copyDraftDateTo || dateRangeLength(copyDraftDateFrom, copyDraftDateTo) !== dateRangeLength(selectedSchedule?.dateFrom, selectedSchedule?.dateTo)}
            onClick={copyPublishedScheduleAsDraft}
          >
            Utwórz kopię roboczą
          </Button>
        </Modal.Footer>
      </Modal>

      <SchedulePrintDialog
        show={showPrint}
        onHide={() => setShowPrint(false)}
        schedule={selectedSchedule}
        referenceData={referenceData}
      />
    </div>
  );
};

export default WorkSchedule;



